// Filtros del directorio del marketplace como módulo PURO: la página los lee
// de la URL (searchParams) en el servidor y el cliente los mantiene en la URL.
// La semántica de applyFilters está CALCADA de filterMerchants
// (app/order/MarketplaceClient.tsx), que hoy delega aquí — una sola verdad.
//
// Decisión de canonical (plan 2026-08-09-marketplace-redytoeat-f1-f2, P1-7):
// las únicas combinaciones indexables por sí mismas son UN filtro de la
// whitelist (cuisine válida sola, o city sola) — valor long-tail real. Todo lo
// demás (q, open, 2+ filtros) canonicaliza a la base /order para que Google
// consolide en vez de rastrear variantes infinitas.

import { MARKETPLACE_CUISINE_TYPES, CUISINE_LABELS } from "@/lib/marketplace/constants"
import type { MarketplaceCuisine } from "@/lib/marketplace/constants"
import type { MarketplaceMerchant } from "@/lib/marketplace/types"

export type MarketplaceFilters = {
  cuisine: MarketplaceCuisine | null
  city: string | null
  q: string
  open: boolean
  /** F3: orden por cercanía. La URL lleva SOLO `sort=near` — jamás coordenadas
   *  (la posición del visitante no viaja en URL ni al servidor). */
  sort: "near" | null
}

export const EMPTY_FILTERS: MarketplaceFilters = {
  cuisine: null,
  city: null,
  q: "",
  open: false,
  sort: null,
}

/** Forma cruda de searchParams en App Router (ya awaited) o de
 *  Object.fromEntries(useSearchParams().entries()) en el cliente. */
export type RawSearchParams = Record<string, string | string[] | undefined>

const first = (v: string | string[] | undefined): string | undefined =>
  Array.isArray(v) ? v[0] : v

const isCuisine = (v: string): v is MarketplaceCuisine =>
  (MARKETPLACE_CUISINE_TYPES as readonly string[]).includes(v)

/** Lee los filtros de la URL. Valores inválidos (cocina fuera de las 12,
 *  city/q en blanco) degradan a "sin filtro" — jamás revientan la página. */
export function parseFilters(sp: RawSearchParams): MarketplaceFilters {
  const rawCuisine = first(sp.cuisine)
  const rawCity = first(sp.city)?.trim()
  const rawQ = first(sp.q)?.trim()
  return {
    cuisine: rawCuisine && isCuisine(rawCuisine) ? rawCuisine : null,
    city: rawCity ? rawCity : null,
    q: rawQ ?? "",
    open: first(sp.open) === "1",
    sort: first(sp.sort) === "near" ? "near" : null,
  }
}

function cuisineLabelOf(m: MarketplaceMerchant): string {
  return m.marketplace_cuisine && m.marketplace_cuisine in CUISINE_LABELS
    ? CUISINE_LABELS[m.marketplace_cuisine as keyof typeof CUISINE_LABELS]
    : ""
}

/**
 * Filtra la lista con la misma semántica que el cliente aplica en vivo:
 *  - city: igualdad case-insensitive contra `m.city`
 *  - cuisine: igualdad estricta contra `m.marketplace_cuisine`
 *  - open: solo `m.isOpen`
 *  - q: substring case-insensitive contra nombre + label de cocina + city +
 *    platillos, en unión con los ids del server (pg_trgm, tolerante a typos)
 */
export function applyFilters(
  merchants: MarketplaceMerchant[],
  filters: MarketplaceFilters,
  extraSearchIds?: Set<string>,
): MarketplaceMerchant[] {
  const q = filters.q.trim().toLowerCase()
  return merchants.filter((m) => {
    if (filters.city && m.city?.toLowerCase() !== filters.city.toLowerCase()) return false
    if (filters.cuisine && m.marketplace_cuisine !== filters.cuisine) return false
    if (filters.open && !m.isOpen) return false
    if (q) {
      const haystack =
        `${m.name} ${cuisineLabelOf(m)} ${m.city ?? ""} ${m.dish_names.join(" ")}`.toLowerCase()
      if (!haystack.includes(q) && !extraSearchIds?.has(m.id)) return false
    }
    return true
  })
}

/**
 * Firma posicional histórica (vivía en MarketplaceClient.tsx; se mudó aquí en
 * F2 porque el client component ahora arrastra next-intl/ESM y jest no lo
 * parsea). Solo adapta argumentos — la verdad es applyFilters.
 */
export function filterMerchants(
  merchants: MarketplaceMerchant[],
  city: string | null,
  cuisine: string | null,
  search = "",
  openNow = false,
  extraSearchIds?: Set<string>,
): MarketplaceMerchant[] {
  return applyFilters(
    merchants,
    { city, cuisine: cuisine as MarketplaceCuisine | null, q: search, open: openNow, sort: null },
    extraSearchIds,
  )
}

/** Serializa SOLO lo activo, en orden estable (URLs canónicas deterministas). */
export function filtersToSearchParams(filters: MarketplaceFilters): URLSearchParams {
  const params = new URLSearchParams()
  if (filters.cuisine) params.set("cuisine", filters.cuisine)
  if (filters.city) params.set("city", filters.city)
  if (filters.q.trim()) params.set("q", filters.q.trim())
  if (filters.open) params.set("open", "1")
  if (filters.sort) params.set("sort", filters.sort)
  return params
}

/**
 * "self": la URL filtrada es su propio canonical (indexable). "base": el
 * canonical apunta a la base del directorio. Sin filtros la base ES la página,
 * así que "self" y "base" coinciden — se reporta "self".
 */
export function canonicalForFilters(filters: MarketplaceFilters): "self" | "base" {
  // F3: `sort=near` ordena por la posición del visitante — contenido por
  // usuario, jamás indexable por sí mismo. Gana sobre la whitelist.
  if (filters.sort) return "base"
  if (filters.q.trim() || filters.open) return "base"
  if (filters.cuisine && filters.city) return "base"
  return "self"
}
